import { instance } from "api/apiBase";
import {
  ReviewUnderwritingFindingsFilters,
  UnderwritingAgentStatus,
  UnderwritingFinding,
  UnderwritingReportKey
} from "./types";

const basePath = (applicationId: string) =>
  `/portal/v1/applications/${applicationId}/underwriting_agent`;

export const underwritingAgentReviewQueryKey = (applicationId: string) => [
  "UNDERWRITING_AGENT_REVIEW",
  applicationId
];
export const underwritingAgentStatusQueryKey = (applicationId: string) => [
  ...underwritingAgentReviewQueryKey(applicationId),
  "STATUS"
];
export const underwritingFindingsQueryKey = (applicationId: string) => [
  ...underwritingAgentReviewQueryKey(applicationId),
  "FINDINGS"
];

export const underwritingFindingsApi = {
  getStatus: async (
    applicationId: string
  ): Promise<UnderwritingAgentStatus> => {
    const { data } = await instance.get<UnderwritingAgentStatus>(
      `${basePath(applicationId)}/status`
    );
    return data;
  },

  /** Runs the agent again on an applicant's report whose last check failed; it only queues the check. */
  rerun: async ({
    applicationId,
    applicantId,
    reportKey
  }: {
    applicationId: string;
    applicantId: number;
    reportKey: UnderwritingReportKey;
  }): Promise<void> => {
    await instance.post(`${basePath(applicationId)}/rerun`, {
      applicant_id: applicantId,
      report_key: reportKey
    });
  },

  /** The application's current findings, both to review and reviewed. */
  list: async (applicationId: string): Promise<UnderwritingFinding[]> => {
    const { data } = await instance.get<{ items: UnderwritingFinding[] }>(
      `${basePath(applicationId)}/findings`
    );
    return data.items;
  },

  review: async ({
    applicationId,
    findingId
  }: {
    applicationId: string;
    findingId: number;
  }): Promise<UnderwritingFinding> => {
    const { data } = await instance.post<UnderwritingFinding>(
      `${basePath(applicationId)}/findings/${findingId}/review`
    );
    return data;
  },

  undoReview: async ({
    applicationId,
    findingId
  }: {
    applicationId: string;
    findingId: number;
  }): Promise<UnderwritingFinding> => {
    const { data } = await instance.delete<UnderwritingFinding>(
      `${basePath(applicationId)}/findings/${findingId}/review`
    );
    return data;
  },

  /** Marks every finding to review reviewed, narrowed to an applicant and/or report when given. */
  reviewAll: async ({
    applicationId,
    ...filters
  }: ReviewUnderwritingFindingsFilters & {
    applicationId: string;
  }): Promise<{ reviewed_count: number }> => {
    const { data } = await instance.post<{ reviewed_count: number }>(
      `${basePath(applicationId)}/findings/review`,
      filters
    );
    return data;
  }
};
